import { ModalNestedPosition } from "../../../../../util/modal-nested-position";
import { CategoryEditorForm } from "./category-editor-form";
import { CategoryEditorRender } from "./category-editor-render";

type CategoryReviewEditorCoreHandler = import("../category-review-editor-handler").CategoryReviewEditorHandler;
type QuestionOptionLanguage = "th" | "en" | "ja";
type CategoryQuestionOptionRow = { th: string; en: string; ja: string };
type CategoryReviewQuestionLike = Record<string, unknown> & {
    _isDeleted?: boolean;
    _isEditing?: boolean;
    _draftOptionTh?: string[];
    _draftOptionEn?: string[];
    _draftOptionJa?: string[];
    option_th?: string[];
    option_en?: string[];
    option_ja?: string[];
};

const toInt = (value: unknown, fallback = 0): number => {
    const n = Number.parseInt(String(value ?? ""), 10);
    return Number.isFinite(n) ? n : fallback;
};

const OTHERS_SUFFIX = 99;
const OTHERS_NAME = "Others";
const CHILD_CODE_DIGITS = 2;

export class CategoryEditorHandler {
    public coreHandler: CategoryReviewEditorCoreHandler;

    private readonly form: CategoryEditorForm;

    public constructor(coreHandler: CategoryReviewEditorCoreHandler) {
        this.coreHandler = coreHandler;
        this.form = new CategoryEditorForm(document, coreHandler.modalEl);
    }

    public handleOpenClick(event: Event): void {
        this.coreHandler.handleOpenClick(event);
    }

    public handleOverlayClick(event: Event): void {
        this.coreHandler.handleOverlayClick(event);
    }

    public handleCloseClick(event: Event): void {
        this.coreHandler.handleCloseClick(event);
    }

    public handleTabParentClick(event: Event): void {
        this.coreHandler.handleTabParentClick(event);
    }

    public handleTabChildClick(event: Event): void {
        this.coreHandler.handleTabChildClick(event);
    }

    public handleTabReviewClick(event: Event): void {
        this.coreHandler.handleTabReviewClick(event);
    }

    public onParentPanelInput(event: Event): void {
        this.coreHandler.onParentPanelInput(event);
    }

    public onChildPanelInput(event: Event): void {
        this.coreHandler.onChildPanelInput(event);
    }

    public onChildPanelChange(event: Event): void {
        this.coreHandler.onChildPanelChange(event);
    }

    public onReviewPanelInput(event: Event): void {
        this.coreHandler.onReviewPanelInput(event);
    }

    public onReviewPanelChange(event: Event): void {
        this.coreHandler.onReviewPanelChange(event);
    }

    public onReviewPanelClick(event: Event): void {
        this.coreHandler.onReviewPanelClick(event);
    }

    public onParentPanelClick(event: Event): void {
        this.coreHandler.onParentPanelClick(event);
    }

    public onChildPanelClick(event: Event): void {
        this.coreHandler.onChildPanelClick(event);
    }

    public switchTab(tabKey: string): void {
        const c = this.coreHandler;
        c.activeTab = tabKey === "child" || tabKey === "review" ? tabKey : "parent";
        const isParent = c.activeTab === "parent";
        const isChild = c.activeTab === "child";
        const isReview = c.activeTab === "review";
        c.tabParentEl?.classList.toggle("is-active", isParent);
        c.tabChildEl?.classList.toggle("is-active", isChild);
        c.tabReviewEl?.classList.toggle("is-active", isReview);
        c.tabParentEl?.setAttribute("aria-selected", isParent ? "true" : "false");
        c.tabChildEl?.setAttribute("aria-selected", isChild ? "true" : "false");
        c.tabReviewEl?.setAttribute("aria-selected", isReview ? "true" : "false");
        if (c.panelParentEl instanceof HTMLElement) {
            c.panelParentEl.hidden = !isParent;
            c.panelParentEl.classList.toggle("is-active", isParent);
        }
        if (c.panelChildEl instanceof HTMLElement) {
            c.panelChildEl.hidden = !isChild;
            c.panelChildEl.classList.toggle("is-active", isChild);
        }
        if (c.panelReviewEl instanceof HTMLElement) {
            c.panelReviewEl.hidden = !isReview;
            c.panelReviewEl.classList.toggle("is-active", isReview);
        }
        this.syncFooterAddButton();
        this.updateAddEntryModalContext();
        c.setStatus("");
    }

    public async open(): Promise<void> {
        const c = this.coreHandler;
        if (c.isLoading) return;
        document.body.classList.add("category-review-editor-open");
        c.setBusy(false);
        this.closeAddEntryModal();

        ModalNestedPosition.openOverlayModalAsNested({
            overlayEl: c.overlayEl,
            modalEl: c.modalEl,
        });
        this.syncFooterAddButton();
        await c.reloadData();
    }

    public close(): void {
        const c = this.coreHandler;
        document.body.classList.remove("category-review-editor-open");
        c.isLoading = false;
        c.isSubmitting = false;
        c.setModalLoading(false);
        c.setBusy(false);
        this.closeAddEntryModal();
        c.closeQuestionOptionModal();
        c.closeReviewSettingsModal();
        c.modalEl.classList.remove("is-nested-modal");
        delete c.modalEl.dataset.modalDepth;
        c.modalEl.style.removeProperty("--nested-offset-x");
        c.modalEl.style.removeProperty("--nested-offset-y");
        ModalNestedPosition.closeOverlayModal({
            overlayEl: c.overlayEl,
            modalEl: c.modalEl,
        });
        c.modalEl.style.removeProperty("left");
        c.modalEl.style.removeProperty("top");
        c.modalEl.style.removeProperty("right");
        c.modalEl.style.removeProperty("bottom");
    }

    public syncFooterAddButton(): void {
        const c = this.coreHandler;
        if (!(c.addBtnEl instanceof HTMLElement)) return;
        const canAdd = c.activeTab === "parent" || c.activeTab === "child" || c.activeTab === "review";
        c.addBtnEl.hidden = !canAdd;
        c.addBtnEl.toggleAttribute("disabled", !canAdd || c.isLoading || c.isSubmitting);
    }

    public getOthersChildIdFromParentId(parentId: unknown): number {
        const base = Math.floor(Math.abs(toInt(parentId, 0)) / 100) * 100;
        return base + OTHERS_SUFFIX;
    }

    public collectActiveCategoryIds(): Set<number> {
        const c = this.coreHandler;
        const ids = new Set<number>();
        c.parents.forEach((parent) => {
            if (parent._isDeleted) return;
            const id = toInt(parent.category_id, 0);
            if (id > 0) ids.add(id);
        });
        c.children.forEach((child) => {
            if (child._isDeleted) return;
            const id = toInt(child.category_id, 0);
            if (id > 0) ids.add(id);
        });
        return ids;
    }

    public getNextParentRawCode(): number {
        const c = this.coreHandler;
        const maxRaw = c.parents
            .filter((parent) => !parent._isDeleted)
            .map((parent) => Math.floor(toInt(parent.category_id, 0) / 100))
            .filter((raw) => raw > 0 && raw < OTHERS_SUFFIX)
            .reduce((max, raw) => (raw > max ? raw : max), 0);
        const nextRaw = maxRaw + 1;
        if (nextRaw >= OTHERS_SUFFIX) return OTHERS_SUFFIX - 1;
        return nextRaw > 0 ? nextRaw : 1;
    }

    public getNextChildRawCode(parentId: unknown): number {
        const c = this.coreHandler;
        const safeParentId = toInt(parentId, 0);
        if (safeParentId <= 0) return 1;
        const parentBase = Math.floor(Math.abs(safeParentId) / 100) * 100;
        const maxRaw = c.children
            .filter((child) => !child._isDeleted)
            .filter((child) => toInt(child.parent_category_id, 0) === safeParentId)
            .map((child) => toInt(child.category_id, 0) - parentBase)
            .filter((raw) => raw > 0 && raw < OTHERS_SUFFIX)
            .reduce((max, raw) => (raw > max ? raw : max), 0);
        const nextRaw = maxRaw + 1;
        if (nextRaw >= OTHERS_SUFFIX) return OTHERS_SUFFIX - 1;
        return nextRaw > 0 ? nextRaw : 1;
    }

    public formatChildRawCode(value: unknown): string {
        const raw = toInt(value, 0);
        if (raw <= 0) return "";
        return String(raw).padStart(CHILD_CODE_DIGITS, "0");
    }

    public updateAddEntryModalContext(): void {
        const c = this.coreHandler;
        if (!(c.addEntryModalEl instanceof HTMLElement)) return;
        const titleEl = this.form.getAddEntryTitle(c.addEntryModalEl);
        const prefixEl = this.form.getAddEntryCodePrefix(c.addEntryModalEl);
        const suffixEl = this.form.getAddEntryCodeSuffix(c.addEntryModalEl);
        const isChild = c.activeTab === "child";
        if (titleEl instanceof HTMLElement) {
            titleEl.textContent = isChild ? "Add (Child Category)" : "Add (Parent Category)";
        }
        if (c.addEntrySubmitBtnEl instanceof HTMLElement) {
            c.addEntrySubmitBtnEl.textContent = isChild ? "Add (Child Category)" : "Add (Parent Category)";
        }
        if (c.addEntryCodeEl instanceof HTMLInputElement) {
            c.addEntryCodeEl.maxLength = 2;
            c.addEntryCodeEl.placeholder = "";
            if (isChild) {
                c.addEntryCodeEl.value = this.formatChildRawCode(c.addEntryCodeEl.value);
            }
        }
        if (prefixEl instanceof HTMLElement) {
            if (isChild) {
                const parentId = toInt(c.selectedChildParentId, 0);
                prefixEl.textContent = parentId > 0 ? String(Math.floor(parentId / 100)) : "-";
                prefixEl.hidden = false;
            } else {
                prefixEl.textContent = "";
                prefixEl.hidden = true;
            }
        }
        if (suffixEl instanceof HTMLElement) {
            suffixEl.textContent = "00";
            suffixEl.hidden = isChild;
        }
    }

    public handleAddEntryModalInput(event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        const action = String(target.dataset.action || "");
        if (action !== "add-entry-code") return;
        const digitsOnly = String(target.value || "")
            .replace(/\D+/g, "")
            .slice(0, 2);
        if (target.value !== digitsOnly) {
            target.value = digitsOnly;
        }
    }

    public handleAddEntryModalChange(event: Event): void {
        const c = this.coreHandler;
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        const action = String(target.dataset.action || "");
        if (action !== "add-entry-code") return;
        if (c.activeTab !== "child") return;
        target.value = this.formatChildRawCode(target.value);
    }

    public openAddEntryModal(): void {
        const c = this.coreHandler;
        if (!(c.addEntryModalEl instanceof HTMLElement)) return;
        if (c.addEntryCodeEl instanceof HTMLInputElement) {
            const initialRaw =
                c.activeTab === "child"
                    ? this.getNextChildRawCode(c.selectedChildParentId)
                    : this.getNextParentRawCode();
            c.addEntryCodeEl.value = c.activeTab === "child" ? this.formatChildRawCode(initialRaw) : String(initialRaw);
            c.addEntryCodeEl.maxLength = 2;
        }
        if (c.addEntryNameEl instanceof HTMLInputElement) c.addEntryNameEl.value = "";
        this.updateAddEntryModalContext();
        ModalNestedPosition.openModal({
            modalEl: c.addEntryModalEl,
            displayValue: "flex",
        });
    }

    public closeAddEntryModal(): void {
        const c = this.coreHandler;
        if (!(c.addEntryModalEl instanceof HTMLElement)) return;
        ModalNestedPosition.closeModal({ modalEl: c.addEntryModalEl });
    }

    public formatCommaSeparatedOptions(list: unknown[] | null | undefined): string {
        if (!Array.isArray(list)) return "";
        return list
            .map((item) => String(item || "").trim())
            .filter((item) => item !== "")
            .join(", ");
    }

    public buildQuestionOptionRows(question: CategoryReviewQuestionLike): CategoryQuestionOptionRow[] {
        const thList = this.getQuestionOptionsByLang(question, "th");
        const enList = this.getQuestionOptionsByLang(question, "en");
        const jaList = this.getQuestionOptionsByLang(question, "ja");
        const rowCount = Math.max(1, thList.length, enList.length, jaList.length);
        return Array.from({ length: rowCount }, (_, index) => ({
            th: String(thList[index] || ""),
            en: String(enList[index] || ""),
            ja: String(jaList[index] || ""),
        }));
    }

    public renderQuestionOptionRows(rows: CategoryQuestionOptionRow[]): void {
        const c = this.coreHandler;
        if (!(c.questionOptionRowsEl instanceof HTMLElement)) return;
        CategoryEditorRender.renderQuestionOptionRows(c.questionOptionRowsEl, rows);
    }

    public collectQuestionOptionRowsFromModal(): { th: string[]; en: string[]; ja: string[] } {
        const c = this.coreHandler;
        if (!(c.questionOptionRowsEl instanceof HTMLElement)) return { th: [], en: [], ja: [] };
        const rows = this.form.getQuestionOptionRows(c.questionOptionRowsEl);
        const th: string[] = [];
        const en: string[] = [];
        const ja: string[] = [];
        rows.forEach((rowEl: Element) => {
            const thInput = this.form.getQuestionOptionRowThInput(rowEl);
            const enInput = this.form.getQuestionOptionRowEnInput(rowEl);
            const jaInput = this.form.getQuestionOptionRowJaInput(rowEl);
            const thValue = thInput instanceof HTMLInputElement ? String(thInput.value || "").trim() : "";
            const enValue = enInput instanceof HTMLInputElement ? String(enInput.value || "").trim() : "";
            const jaValue = jaInput instanceof HTMLInputElement ? String(jaInput.value || "").trim() : "";
            if (thValue === "" && enValue === "" && jaValue === "") return;
            th.push(thValue);
            en.push(enValue);
            ja.push(jaValue);
        });
        return { th, en, ja };
    }

    public getQuestionOptionsByLang(
        question: CategoryReviewQuestionLike | null | undefined,
        lang: QuestionOptionLanguage,
    ): string[] {
        const optionKeyByLang: Record<QuestionOptionLanguage, "option_th" | "option_en" | "option_ja"> = {
            th: "option_th",
            en: "option_en",
            ja: "option_ja",
        };
        const draftKeyByLang: Record<QuestionOptionLanguage, "_draftOptionTh" | "_draftOptionEn" | "_draftOptionJa"> = {
            th: "_draftOptionTh",
            en: "_draftOptionEn",
            ja: "_draftOptionJa",
        };
        const optionKey = optionKeyByLang[lang];
        const draftKey = draftKeyByLang[lang];
        const draft = question?.[draftKey];
        if (Array.isArray(draft)) return draft;
        const original = question?.[optionKey];
        return Array.isArray(original) ? original : [];
    }

    public openQuestionOptionModal(configId: number, questionId: number): void {
        const c = this.coreHandler;
        if (!(c.questionOptionModalEl instanceof HTMLElement)) return;
        const question = c.getQuestionRowById(questionId);
        if (!question || question._isDeleted) return;
        c.activeQuestionOptionTarget = { configId, questionId };
        this.renderQuestionOptionRows(this.buildQuestionOptionRows(question));
        ModalNestedPosition.openModal({
            modalEl: c.questionOptionModalEl,
            displayValue: "flex",
        });
    }

    public closeQuestionOptionModal(): void {
        const c = this.coreHandler;
        if (!(c.questionOptionModalEl instanceof HTMLElement)) return;
        c.activeQuestionOptionTarget = null;
        if (c.questionOptionRowsEl instanceof HTMLElement)
            CategoryEditorRender.clearQuestionOptionRows(c.questionOptionRowsEl);
        ModalNestedPosition.closeModal({ modalEl: c.questionOptionModalEl });
    }

    public handleQuestionOptionModalClick(event: Event): void {
        const c = this.coreHandler;
        if (!(c.questionOptionModalEl instanceof HTMLElement)) return;
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        const addRowBtn = this.form.getQuestionOptionAddRowButton(target);
        if (addRowBtn instanceof HTMLElement) {
            event.preventDefault();
            const current = this.collectQuestionOptionRowsFromModal();
            const rowCount = Math.max(current.th.length, current.en.length, current.ja.length);
            const rows = Array.from({ length: rowCount }, (_, index) => ({
                th: String(current.th[index] || ""),
                en: String(current.en[index] || ""),
                ja: String(current.ja[index] || ""),
            }));
            rows.push({ th: "", en: "", ja: "" });
            this.renderQuestionOptionRows(rows);
            return;
        }
        const removeBtn = this.form.getQuestionOptionRemoveRowButton(target);
        if (removeBtn instanceof HTMLElement) {
            event.preventDefault();
            const rowEl = this.form.getQuestionOptionRow(removeBtn);
            if (!(rowEl instanceof HTMLElement)) return;
            rowEl.remove();
            const remainingRows = c.questionOptionRowsEl ? this.form.getQuestionOptionRows(c.questionOptionRowsEl) : [];
            if (!remainingRows || remainingRows.length === 0) {
                this.renderQuestionOptionRows([{ th: "", en: "", ja: "" }]);
            } else {
                Array.from(remainingRows).forEach((el: Element, index) => {
                    el.setAttribute("data-row-index", String(index + 1));
                    const noEl = this.form.getQuestionOptionRowNo(el);
                    if (noEl instanceof HTMLElement) noEl.textContent = String(index + 1);
                });
            }
            return;
        }
        if (target === c.questionOptionModalEl) this.closeQuestionOptionModal();
    }

    public handleQuestionOptionClose(event: Event): void {
        event.preventDefault();
        this.closeQuestionOptionModal();
    }

    public handleQuestionOptionSubmit(event: Event): void {
        const c = this.coreHandler;
        event.preventDefault();
        const target = c.activeQuestionOptionTarget;
        if (!target) return;
        const question = c.getQuestionRowById(target.questionId);
        if (!question || question._isDeleted) {
            this.closeQuestionOptionModal();
            return;
        }
        const rowValues = this.collectQuestionOptionRowsFromModal();
        question._isEditing = true;
        question._draftOptionTh = rowValues.th;
        question._draftOptionEn = rowValues.en;
        question._draftOptionJa = rowValues.ja;
        this.closeQuestionOptionModal();
        c.renderer.renderReviewTable(c);
    }

    public handleFooterAddClick(event: Event): void {
        const c = this.coreHandler;
        event.preventDefault();
        if (c.activeTab === "review") {
            void c.createReviewQuestion();
            return;
        }
        if (c.activeTab !== "parent" && c.activeTab !== "child") return;
        this.openAddEntryModal();
    }

    public handleAddEntryModalClick(event: Event): void {
        const c = this.coreHandler;
        if (!(c.addEntryModalEl instanceof HTMLElement)) return;
        if (event.target !== c.addEntryModalEl) return;
        this.closeAddEntryModal();
    }

    public handleAddEntrySubmit(event: Event): void {
        const c = this.coreHandler;
        event.preventDefault();
        if (c.isLoading || c.isSubmitting) return;
        const rawCode = toInt(c.addEntryCodeEl?.value, 0);
        const name = String(c.addEntryNameEl?.value || "").trim();
        const parentId = c.activeTab === "child" ? toInt(c.selectedChildParentId, 0) : 0;
        const hasParent = c.activeTab === "child";
        if (rawCode <= 0) return c.setStatus("Code is required", "error");
        if (rawCode > 99) return c.setStatus("Code must be 2 digits (01-99)", "error");
        if (name === "") return c.setStatus("Name is required", "error");
        if (hasParent) {
            if (parentId <= 0) return c.setStatus("Select parent category in Child Categories tab", "error");
            const parent = c.parentById.get(parentId);
            if (!parent) return c.setStatus("Invalid parent category", "error");
            const parentBase = Math.floor(Math.abs(parentId) / 100) * 100;
            const code = parentBase + rawCode;
            const activeIds = this.collectActiveCategoryIds();
            if (activeIds.has(code)) return c.setStatus(`Duplicate category_id: ${code}`, "error");
            const configId = toInt(parent.review_config_id, 0);
            c.children.push(
                c.normalizeChildRow({
                    category_id: code,
                    category_name: name,
                    parent_category_id: parentId,
                    review_config_id: configId > 0 ? configId : undefined,
                    _isNew: true,
                    _isDeleted: false,
                    _isEditing: true,
                    _draftName: name,
                    _isLocked: c.isLockedOthersChild({ category_id: code, category_name: name }),
                }),
            );
            c.reindexLocalState();
            c.ensureChildParentSelection();
            c.renderer.renderChildTable(c);
            c.renderer.renderReviewTable(c);
            this.closeAddEntryModal();
            c.setStatus("Added 1 row (not saved)", "ok");
            return;
        }
        const code = rawCode * 100;
        const activeIds = this.collectActiveCategoryIds();
        if (activeIds.has(code)) return c.setStatus(`Duplicate category_id: ${code}`, "error");
        const othersChildId = this.getOthersChildIdFromParentId(code);
        if (activeIds.has(othersChildId) || code === othersChildId) {
            return c.setStatus(`Duplicate Others child category_id: ${othersChildId}`, "error");
        }
        c.parents.push(
            c.normalizeParentRow({
                category_id: code,
                category_name: name,
                parent_category_id: null,
                _isNew: true,
                _isDeleted: false,
                _isEditing: true,
                _draftName: name,
            }),
        );
        c.children.push(
            c.normalizeChildRow({
                category_id: othersChildId,
                category_name: OTHERS_NAME,
                parent_category_id: code,
                review_config_id: 99,
                _isNew: true,
                _isDeleted: false,
                _isEditing: false,
                _draftName: OTHERS_NAME,
                _isLocked: true,
                _isSystemOthers: true,
            }),
        );
        c.reindexLocalState();
        c.ensureChildParentSelection();
        c.renderer.renderParentTable(c);
        c.renderer.renderChildTable(c);
        c.renderer.renderReviewTable(c);
        this.closeAddEntryModal();
        c.setStatus("Added parent and --99 Others row (not saved)", "ok");
    }

    public handleAddEntryClose(event: Event): void {
        event.preventDefault();
        this.closeAddEntryModal();
    }

    public handleSaveClick(event: Event): Promise<void> | void {
        return this.coreHandler.handleSaveClick(event) as Promise<void> | void;
    }
}
